import {
  CascaderField,
  ContentItem,
  ContentItems,
  ContentWrapper,
  type OperationType,
  SelectField,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { SelectMinistryField } from '@/features/master-table/components';
import { useNestedExpenditureBudgetPurposeOptions } from '@/features/master-table/hooks';
import {
  SkipAdministrativeReformTeamEvaluationReasonLabelModifier,
  skipAdministrativeReformTeamEvaluationReason,
} from '@/features/project/projects/_models/project';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import {
  ProjectCategoryLabelModifier,
  projectCategoryEnum,
} from '@/utils/enum';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';
import type { RsFormSchema } from '../../_model/project-form';
import { FiscalYearField } from '../fields/FiscalYearField';
import { OtherResponsibleOrganizationsArrayField } from '../fields/OtherResponsibleOrganizationsArrayField';
import { PeriodAndStartEndCheckField } from '../fields/PeriodAndStartEndCheckField';
import { PoliciesArrayField } from '../fields/PoliciesArrayField';
import { RelatedOrganizationArrayField } from '../fields/RelatedOrganizationArrayField';
import { SnapshotField } from '../fields/SnapshotField';
import { SortOrderField } from '../fields/SortOrderField';

type FormValues = z.infer<typeof RsFormSchema>;
type InitialFormValues = {
  data: Partial<FormValues['data']>;
};

type Props = {
  operation: OperationType;
};

export const RsSkippedReviewProjectForm: FC<Props> = ({ operation }) => {
  const {
    control,
    formState: { errors },
    watch,
    resetField,
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const { t } = useTranslation();

  const [fiscalYear, fiscalYearOfSnapshot] = watch([
    'data.fiscalYear',
    'data.fiscalYearOfSnapshot',
  ]);
  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const selectFiscalYear = fiscalYear?.year().toString();
  const expenditureBudgetPurposesOptions =
    useNestedExpenditureBudgetPurposeOptions({
      params: { type: 'majorMatter', referenceFiscalYear: selectFiscalYear },
      enabled: !!selectFiscalYear,
    });

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  return (
    <>
      <ContentWrapper paddingTop={0}>
        <ContentItem borderBottom="0" paddingBottom={0} paddingTop={0}>
          <ContentItems
            items={[
              <FiscalYearField
                key="data.fiscalYear"
                operation={operation}
                onChange={() => {
                  resetField('data.organizationIds', { defaultValue: [] });
                  resetField('data.majorExpenditureBudgetPurposeIds', {
                    defaultValue: [],
                  });
                  resetField('data.policiesForProject', { defaultValue: [] });
                }}
              />,
              <SortOrderField
                key="data.sortOrder"
                hint={t('hint-project-order-number-for-review-skipped')}
              />,
              <SnapshotField key="data.snapshotId" />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('organization')}>
        <ContentItem>
          <ContentItems
            mode="fillRight"
            items={[
              <SelectMinistryField
                key="data.ministryId"
                name="data.ministryId"
                control={control}
                errors={errors}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem>
          <ContentItems
            items={[
              <CascaderField
                key="data.organizationIds"
                name="data.organizationIds"
                label={t('organization-in-charge')}
                hint={t('hint-organization-in-charge-for-rs-skipped')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                enableIntermediate
                disabled={!fiscalYear}
                required
                loading={organizationOptions.isPending}
                textNotInOption={t('not-exist-in-organization-options')}
              />,
            ]}
          />
          <ContentItems
            items={[
              <UserSelectField
                key="data.peopleInChargeIds"
                name="data.peopleInChargeIds"
                label={t('people-in-charge')}
                hint={t('hint-people-in-charge-for-review-skipped')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                snapshotId={snapshotId ?? undefined}
                useNotInOption
                required
              />,
              <UserSelectField
                key="data.businessManagerIds"
                mode="multiple"
                name="data.businessManagerIds"
                label={t('business-manager')}
                hint={t('hint-business-manager-for-review-skipped')}
                placeholder={t('placeholder-business-manager')}
                control={control}
                errors={errors}
                organizationFilter="modal"
                snapshotId={snapshotId ?? undefined}
                useNotInOption
                required
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <OtherResponsibleOrganizationsArrayField type="RS" />
          <RelatedOrganizationArrayField
            type="RS"
            reviewType="skipped-review"
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('basic-information')}>
        <ContentItem>
          <ContentItems
            items={[
              <SelectField
                key="data.skipAdministrativeReformTeamEvaluationReason"
                name="data.skipAdministrativeReformTeamEvaluationReason"
                label={t('skip-administrative-reform-team-evaluation-reason')}
                control={control}
                errors={errors}
                options={skipAdministrativeReformTeamEvaluationReason.options.map(
                  (value) => ({
                    value,
                    label:
                      SkipAdministrativeReformTeamEvaluationReasonLabelModifier[
                        value
                      ],
                  }),
                )}
                required
              />,
              <TextAreaInputField
                key="data.skipAdministrativeReformTeamEvaluationReasonSupplement"
                name="data.skipAdministrativeReformTeamEvaluationReasonSupplement"
                label={t(
                  'skip-administrative-reform-team-evaluation-reason-supplement',
                )}
                hint={t(
                  'hint-skip-administrative-reform-team-evaluation-reason-supplement',
                )}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />

          <ContentItems
            items={[
              <CascaderField
                key="data.majorExpenditureBudgetPurposeIds"
                name="data.majorExpenditureBudgetPurposeIds"
                label={t('expenditure-purpose-level-3-1')}
                control={control}
                options={expenditureBudgetPurposesOptions.data}
                errors={errors}
                disabled={!fiscalYear}
                hint={t('hint-expenditure-purpose-for-rs-skipped')}
                loading={expenditureBudgetPurposesOptions.isPending}
              />,
            ]}
          />

          <ContentItems
            items={[
              <TextInputField
                key="data.name"
                name="data.name"
                autocompleteKey="rsProject.name"
                label={t('project-name')}
                control={control}
                errors={errors}
                required
              />,
            ]}
          />

          <ContentItems
            mode="fillRight"
            items={[
              <SelectField
                key="data.category"
                name="data.category"
                label={t('project-category')}
                hint={t('hint-project-category-for-rs-skipped')}
                control={control}
                errors={errors}
                options={projectCategoryEnum.options.map((value) => ({
                  value,
                  label: ProjectCategoryLabelModifier[value],
                }))}
                required
              />,
            ]}
          />

          <ContentItems
            mode="fullLine"
            items={[
              <PeriodAndStartEndCheckField
                key="data.period-data.isUnknownStart-data.isNoScheduledEnd"
                hint={t('hint-project-start-end-year-for-rs-skipped')}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <PoliciesArrayField />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TextInputField
                key="data.oldProjectNumber"
                name="data.oldProjectNumber"
                label={t('old-project-number')}
                hint={t('hint-old-project-number-for-rs-skipped')}
                control={control}
                errors={errors}
              />,
              <TextAreaInputField
                key="data.remark"
                name="data.remark"
                label={t('remark')}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
